/**
 * Translates the colors from `src/configs/theme.ts` into what MUI expects.
 *
 * Each semantic color becomes an object with `main`, `light`, `dark`, the five
 * translucent variants (`lightOpacity`, `mainOpacity`, …) the design system
 * uses in chips, `skin='light'` avatars, hovers and active states — and
 * `tonalText`, the tone to write with when the background is one of those
 * translucent variants.
 */
import { decomposeColor } from '@mui/material/styles'

import type { Color } from '@configs/theme'

/** The same ratios the template applies over the main tone. */
const LIGHTEN = 0.2
const DARKEN = 0.1

/**
 * The contrast `tonalText` aims for — WCAG AA for body text. A brand color is
 * picked to be seen, not to be read: at 16% over white, `#E8952F` writing in
 * `#E8952F` lands at 2.1:1. So the tone that fills a surface and the tone that
 * writes on it are two different answers, and only the second one is measured.
 */
const TONAL_TEXT_CONTRAST = 4.5

/** The opacity `lightOpacity` uses — the background `tonalText` is measured against. */
const TONAL_BACKGROUND_OPACITY = 0.16

const channelsOf = (color: string): number[] => decomposeColor(color).values.slice(0, 3)

const toHex = (channels: number[]): string =>
  `#${channels
    .map(c =>
      Math.min(255, Math.max(0, Math.round(c)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')
    .toUpperCase()}`

/**
 * Moves the color towards white. `lighten('#7367F0', 0.2)` → `#8F85F3`.
 *
 * MUI ships its own `lighten`/`darken`; these round the channels the way the
 * template's palette was authored, so deriving a tone reproduces the original
 * hex exactly instead of drifting by one unit.
 */
export const lighten = (color: string, ratio: number): string =>
  toHex(channelsOf(color).map(channel => channel + (255 - channel) * ratio))

/** Moves the color towards black. `darken('#7367F0', 0.1)` → `#675DD8`. */
export const darken = (color: string, ratio: number): string =>
  toHex(channelsOf(color).map(channel => channel * (1 - ratio)))

/** The main tone, whether the color is in the short or the long form. */
export const mainColor = (color: Color): string => (typeof color === 'string' ? color : color.main)

/**
 * `#2F3349` → `'47 51 73'`. MUI builds the CSS variables with the channels
 * split apart so opacity can be applied without recomputing the color.
 */
export const channels = (color: string): string => channelsOf(color).map(Math.round).join(' ')

/** WCAG relative luminance. */
const luminance = (color: string): number => {
  const [r, g, b] = channelsOf(color).map(channel => {
    const value = channel / 255

    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })

  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG contrast ratio, from 1 (identical) to 21 (black on white). */
export const contrastRatio = (a: string, b: string): number => {
  const [brighter, darker] = [luminance(a), luminance(b)].sort((x, y) => y - x)

  return (brighter + 0.05) / (darker + 0.05)
}

/** Flattens a translucent color over an opaque one — what the eye ends up seeing. */
const flatten = (color: string, opacity: number, backdrop: string): string => {
  const behind = channelsOf(backdrop)

  return toHex(channelsOf(color).map((channel, index) => channel * opacity + behind[index] * (1 - opacity)))
}

/** Above this luminance a surface reads as light, and text on it has to go darker. */
const LIGHT_SURFACE = 0.18

/**
 * The nearest tone of `color` that reaches `target` against `backdrop`, walking
 * away from the backdrop — darker on a light surface, lighter on a dark one.
 *
 * It walks in 2% steps instead of solving for the ratio because the answer has
 * to stay recognizably the brand color: the first tone that is readable wins,
 * and nothing is darkened further than it needs to be.
 */
export const readableOn = (color: string, backdrop: string, target = TONAL_TEXT_CONTRAST): string => {
  const towardsBlack = luminance(backdrop) > LIGHT_SURFACE
  const move = towardsBlack ? darken : lighten

  for (let ratio = 0; ratio <= 0.9; ratio += 0.02) {
    const candidate = move(color, ratio)

    if (contrastRatio(candidate, backdrop) >= target) return candidate
  }

  return towardsBlack ? '#000000' : '#FFFFFF'
}

/**
 * The full palette of a color. Tones left out are derived from the main one,
 * so declaring `primary: '#7367F0'` is enough.
 *
 * `surface` is the paper of the scheme being built: the tonal background is
 * the color itself at 16% over it, so the same brand color yields a darker
 * `tonalText` in the light scheme and a lighter one in the dark scheme.
 */
export const paletteOf = (color: Color, variableName: string, surface: string) => {
  const config = typeof color === 'string' ? { main: color } : color
  const { main } = config
  const opacity = (value: number) => `rgb(var(--mui-palette-${variableName}-mainChannel) / ${value})`

  return {
    main,
    light: config.light ?? lighten(main, LIGHTEN),
    dark: config.dark ?? darken(main, DARKEN),
    // White keeps the contrast of the six default colors; light colors need an
    // explicit `contrastText` in the theme file.
    contrastText: config.contrastText ?? '#FFF',
    tonalText: readableOn(main, flatten(main, TONAL_BACKGROUND_OPACITY, surface), TONAL_TEXT_CONTRAST),
    lighterOpacity: opacity(0.08),
    lightOpacity: opacity(0.16),
    mainOpacity: opacity(0.24),
    darkOpacity: opacity(0.32),
    darkerOpacity: opacity(0.38)
  }
}
